import { ref } from "vue";

/**
 * Shared, runtime client-tool registry (E3 slice 2).
 *
 * Some chat declaration tools are NOT answered by a human and NOT executed on
 * the .NET server — they are fulfilled in the BROWSER (e.g. the automation
 * editor applies a graph mutation locally because it holds the encryption key
 * the server must never have). The chat composable parks such a tool, looks up
 * a handler registered here, runs it, and feeds the result back to resume the
 * agent run.
 *
 * This module is a plain singleton (imported directly, like `eventBus`) rather
 * than a Nuxt composable, so any module — chat (consumer) and automations
 * (provider) — can share the SAME registry without a layer auto-import
 * dependency. `version` is a reactive counter the chat composable watches so a
 * tool that parked *before* its handler was registered (editor opened late, or
 * a conversation reloaded mid-flow) is still picked up.
 */

export interface ClientToolContext {
  /** The conversation the parked tool belongs to. */
  conversationId: string;
  /** The agent's invocation id for the parked tool (use for idempotent ids). */
  invocationId: string;
}

/**
 * Fulfils a browser-mediated tool call. Returns the JSON-serialisable result fed
 * back to the agent. Throwing is caught by the caller and reported to the agent
 * as a structured error result, so handlers may throw to signal failure.
 */
export type ClientToolHandler = (
  input: Record<string, unknown> | null | undefined,
  context: ClientToolContext,
) => unknown | Promise<unknown>;

const handlers = new Map<string, ClientToolHandler>();

/**
 * Bumped on every register/unregister so reactive watchers re-scan parked tools.
 * Shared Vue ref (vue is deduped across modules in the host apps).
 */
export const clientToolsVersion = ref(0);

/** Registers a handler for `name`, returning an idempotent unregister callback. */
export function registerClientTool(
  name: string,
  handler: ClientToolHandler,
): () => void {
  handlers.set(name, handler);
  clientToolsVersion.value++;
  return () => {
    // Only remove if still the same handler — guards against a late unregister
    // from a torn-down component clobbering a freshly remounted one.
    if (handlers.get(name) === handler) {
      handlers.delete(name);
      clientToolsVersion.value++;
    }
  };
}

/** Removes any handler registered for `name`. */
export function unregisterClientTool(name: string): void {
  if (handlers.delete(name)) {
    clientToolsVersion.value++;
  }
}

/** Whether a browser handler is currently registered for `name`. */
export function hasClientTool(name: string): boolean {
  return handlers.has(name);
}

/** Returns the handler registered for `name`, if any. */
export function getClientTool(name: string): ClientToolHandler | undefined {
  return handlers.get(name);
}

/** Test-only: clears all handlers. */
export function __resetClientTools(): void {
  handlers.clear();
  clientToolsVersion.value++;
}
